import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StatTile } from 'components/ui/stat-tile';
import { Skeleton } from 'components/ui/skeleton';
import { formatGroupedNumber } from 'lib/dateFormat';
import { cn } from 'lib/utils';
import type { OverviewResponse, TestsResponse } from 'modules/analytics';

interface KpiRowProps {
  data?: OverviewResponse;
  isLoading: boolean;
  /** Answers waiting for a manual grade (from the grading queue). */
  pendingAnswers: number;
  /** Integrity events in the window, or null when the plan has no integrity analytics. */
  integrityEvents: number | null;
  /** Started → submitted → graded, from the PRO tests endpoint; absent on other plans. */
  funnel?: TestsResponse['funnel'];
  onReviewIntegrity: () => void;
  /** Phones show the first four figures, two per row. */
  compact?: boolean;
}

const linkClass =
  'self-start rounded-sm text-[12.5px] font-medium text-foreground/80 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const stage = (funnel: TestsResponse['funnel'] | undefined, name: string): number | undefined =>
  funnel?.find((s) => s.stage === name)?.count;

/**
 * The period's headline figures. Subs appear only where the API backs them: the
 * total test count and a respondents-in-period figure are not returned, so those
 * tiles carry no second line.
 */
const KpiRow: React.FC<KpiRowProps> = ({
  data,
  isLoading,
  pendingAnswers,
  integrityEvents,
  funnel,
  onReviewIntegrity,
  compact = false,
}) => {
  const { t } = useTranslation();
  const submitted = stage(funnel, 'submitted');
  const started = stage(funnel, 'started');
  // Nothing graded yet means no average and no pass rate — not a 0%.
  const graded = data && data.completedAttempts > 0 ? data : undefined;
  const value = (node: React.ReactNode) => (isLoading ? <Skeleton className="h-6 w-14" /> : node);

  const tiles: Array<{ key: string; node: React.ReactNode }> = [
    {
      key: 'active',
      node: <StatTile label={t('kpi_active_tests')} value={value(data?.activeTests ?? 0)} />,
    },
    {
      key: 'respondents',
      node: <StatTile label={t('kpi_respondents')} value={value(formatGroupedNumber(data?.totalCandidates ?? 0))} />,
    },
    {
      key: 'submissions',
      node: (
        <StatTile
          label={t('kpi_submissions')}
          // With the funnel we can say how many of the started attempts were handed in.
          value={value(formatGroupedNumber(submitted ?? data?.totalAttempts ?? 0))}
          sub={
            !isLoading && submitted != null && started != null
              ? t('analytics_kpi_started', { n: formatGroupedNumber(started) })
              : undefined
          }
        />
      ),
    },
    {
      key: 'avg',
      node: (
        <StatTile
          label={t('kpi_avg_score')}
          value={value(graded ? `${graded.avgScore}%` : '—')}
          sub={!isLoading && graded ? `${graded.passRate}% ${t('pass')}` : undefined}
        />
      ),
    },
    {
      key: 'pending',
      node: (
        <StatTile
          label={t('kpi_pending_grading')}
          value={value(pendingAnswers)}
          tone={pendingAnswers > 0 ? 'warning' : undefined}
          action={
            pendingAnswers > 0 ? (
              <Link to="/app/grading" className={linkClass}>
                {t('grade')} <span aria-hidden>→</span>
              </Link>
            ) : undefined
          }
        />
      ),
    },
    {
      key: 'integrity',
      node: (
        <StatTile
          label={t('kpi_integrity_events')}
          value={value(integrityEvents ?? '—')}
          tone={integrityEvents ? 'error' : undefined}
          action={
            integrityEvents ? (
              <button type="button" onClick={onReviewIntegrity} className={linkClass}>
                {t('review')} <span aria-hidden>→</span>
              </button>
            ) : undefined
          }
        />
      ),
    },
  ];

  const shown = compact ? tiles.slice(0, 4) : tiles;
  return (
    <div
      data-analytics-kpis
      className={cn('grid gap-3', compact ? 'grid-cols-2 gap-2.5' : 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-6')}
    >
      {shown.map((tile) => (
        <React.Fragment key={tile.key}>{tile.node}</React.Fragment>
      ))}
    </div>
  );
};

export default KpiRow;
